import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Trophy, Sparkles, Target, Star, Zap, Crown, Gift } from 'lucide-react';
import ConfettiCelebration from '@/components/gamification/ConfettiCelebration';
import StreakFlame from '@/components/gamification/StreakFlame';

interface ProgressCelebrationProps {
  streak: number;
  totalCheckIns: number;
  currentProgress: number;
  maxSteps: number;
}

export default function ProgressCelebration({ 
  streak, 
  totalCheckIns, 
  currentProgress, 
  maxSteps 
}: ProgressCelebrationProps) {
  const [showMilestone, setShowMilestone] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [milestoneType, setMilestoneType] = useState<'checkin' | 'streak' | 'complete'>('checkin');
  const progressPercent = (currentProgress / maxSteps) * 100;

  // Check for milestones
  useEffect(() => {
    const milestones = [7, 14, 30, 50, 100];
    if (milestones.includes(totalCheckIns)) {
      setMilestoneType('checkin');
      setShowMilestone(true);
      setShowConfetti(true);
      setTimeout(() => {
        setShowMilestone(false);
        setShowConfetti(false);
      }, 5000);
    }
  }, [totalCheckIns]);

  // Check for completion celebration
  useEffect(() => {
    if (progressPercent === 100 && currentProgress > 0) {
      setMilestoneType('complete');
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  }, [progressPercent, currentProgress]);

  const getMilestoneMessage = () => {
    if (totalCheckIns === 7) return { title: "One Week Strong!", icon: 'party', subtitle: "You've built a powerful foundation" };
    if (totalCheckIns === 14) return { title: "Two Weeks of Growth!", icon: 'star', subtitle: "Habits are forming beautifully" };
    if (totalCheckIns === 30) return { title: "30 Days of Dedication!", icon: 'trophy', subtitle: "You're a wellness warrior now" };
    if (totalCheckIns === 50) return { title: "50 Check-Ins Milestone!", icon: 'sparkles', subtitle: "Halfway to legendary status" };
    if (totalCheckIns === 100) return { title: "100 Check-Ins Legend!", icon: 'crown', subtitle: "You've achieved mastery" };
    return { title: "", icon: '', subtitle: "" };
  };

  const milestone = getMilestoneMessage();

  return (
    <>
      {/* Confetti Effect */}
      <ConfettiCelebration isActive={showConfetti} particleCount={60} />

      {/* Milestone Celebration Modal */}
      <AnimatePresence>
        {showMilestone && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
              <Card className="p-8 bg-gradient-to-br from-primary/20 via-secondary/20 to-accent/20 border-primary/30 backdrop-blur-sm shadow-2xl">
                <div className="flex flex-col items-center gap-4">
                  {/* Animated icon */}
                  <motion.div
                    animate={{ 
                      y: [0, -10, 0],
                      rotate: [0, 5, -5, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="relative"
                  >
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                      {totalCheckIns >= 100 ? (
                        <Crown className="w-10 h-10 text-background" />
                      ) : totalCheckIns >= 50 ? (
                        <Gift className="w-10 h-10 text-background" />
                      ) : (
                        <Trophy className="w-10 h-10 text-background" />
                      )}
                    </div>
                    {/* Sparkle decorations */}
                    <motion.div
                      animate={{ scale: [0, 1, 0], rotate: 360 }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="absolute -top-2 -right-2"
                    >
                      <Sparkles className="w-6 h-6 text-primary" />
                    </motion.div>
                    <motion.div
                      animate={{ scale: [0, 1, 0], rotate: -360 }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                      className="absolute -bottom-2 -left-2"
                    >
                      <Star className="w-5 h-5 text-accent" />
                    </motion.div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-center"
                  >
                    <div className="flex justify-center mb-2">
                      {milestone.icon === 'party' && <Sparkles className="w-10 h-10 text-primary" />}
                      {milestone.icon === 'star' && <Star className="w-10 h-10 text-secondary" />}
                      {milestone.icon === 'trophy' && <Trophy className="w-10 h-10 text-secondary" />}
                      {milestone.icon === 'sparkles' && <Sparkles className="w-10 h-10 text-accent" />}
                      {milestone.icon === 'crown' && <Crown className="w-10 h-10 text-secondary" />}
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-1">{milestone.title}</h3>
                    <p className="text-muted-foreground">{milestone.subtitle}</p>
                  </motion.div>

                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6, type: 'spring' }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30"
                  >
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-primary">+{totalCheckIns * 10} XP Earned</span>
                  </motion.div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {/* Streak Card with Animated Flame */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Card className="p-3 sm:p-4 cursor-pointer overflow-hidden relative group">
            {/* Background glow for high streaks */}
            {streak >= 7 && (
              <motion.div
                animate={{ opacity: [0.1, 0.3, 0.1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-accent/20"
              />
            )}
            
            <div className="flex items-center justify-between relative z-10">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-muted-foreground mb-1">Current Streak</p>
                <div className="flex items-center gap-2 sm:gap-3">
                  <StreakFlame streak={streak} size="sm" showLabel={false} />
                  <div>
                    <span className="text-2xl sm:text-3xl font-bold text-foreground">{streak}</span>
                    <span className="text-xs sm:text-sm text-muted-foreground ml-1">days</span>
                  </div>
                </div>
              </div>
              {streak >= 7 && (
                <motion.div
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-8 h-8 text-primary" />
                </motion.div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2 relative z-10 flex items-center gap-1">
              {streak >= 30 ? <><Zap className="w-3 h-3 text-secondary" /> Unstoppable! You're on fire!</> : 
               streak >= 14 ? <><Trophy className="w-3 h-3 text-secondary" /> Two weeks strong! Keep burning!</> :
               streak >= 7 ? <><Sparkles className="w-3 h-3 text-primary" /> One week warrior!</> :
               streak > 0 ? <><Star className="w-3 h-3 text-accent" /> Growing stronger every day!</> :
               "Start your streak today!"}
            </p>
          </Card>
        </motion.div>

        {/* Total Check-ins Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Card className="p-3 sm:p-4 cursor-pointer overflow-hidden relative group">
            {totalCheckIns >= 30 && (
              <motion.div
                animate={{ opacity: [0.1, 0.2, 0.1] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20"
              />
            )}
            
            <div className="flex items-center justify-between relative z-10">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-muted-foreground mb-1">Total Check-Ins</p>
                <div className="flex items-center gap-2">
                  <motion.div
                    animate={totalCheckIns >= 50 ? { rotate: [0, 360] } : {}}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                  >
                    <Target className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </motion.div>
                  <motion.span
                    key={totalCheckIns}
                    initial={{ scale: 1.5, color: 'hsl(var(--accent))' }}
                    animate={{ scale: 1, color: 'hsl(var(--primary))' }}
                    className="text-2xl sm:text-3xl font-bold"
                  >
                    {totalCheckIns}
                  </motion.span>
                </div>
              </div>
              {totalCheckIns >= 30 && (
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="shrink-0"
                >
                  <Trophy className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </motion.div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2 relative z-10 flex items-center gap-1">
              {totalCheckIns >= 100 ? <><Crown className="w-3 h-3 text-secondary shrink-0" /> <span className="truncate">Legendary status achieved!</span></> :
               totalCheckIns >= 50 ? <><Target className="w-3 h-3 text-primary shrink-0" /> <span className="truncate">Halfway to legend!</span></> :
               totalCheckIns >= 30 ? <><Trophy className="w-3 h-3 text-secondary shrink-0" /> <span className="truncate">One month champion!</span></> :
               totalCheckIns >= 10 ? <><Star className="w-3 h-3 text-primary shrink-0" /> <span className="truncate">Double digits!</span></> :
               <><Sparkles className="w-3 h-3 text-primary shrink-0" /> <span className="truncate">Every check-in counts!</span></>}
            </p>
          </Card>
        </motion.div>

        {/* Today's Progress Card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Card className={`p-3 sm:p-4 cursor-pointer overflow-hidden relative ${
            progressPercent === 100 ? 'border-accent' : ''
          }`}>
            {progressPercent === 100 && (
              <motion.div
                animate={{ opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/20"
              />
            )}
            
            <div className="relative z-10">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1">Today's Progress</p>
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <motion.div
                  animate={progressPercent === 100 ? { 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.2, 1]
                  } : {}}
                  transition={{ duration: 0.5, repeat: progressPercent === 100 ? Infinity : 0, repeatDelay: 2 }}
                >
                  <Zap className={`w-5 h-5 sm:w-6 sm:h-6 ${progressPercent === 100 ? 'text-accent' : 'text-primary'}`} />
                </motion.div>
                <span className="text-2xl sm:text-3xl font-bold text-foreground">
                  {currentProgress}/{maxSteps}
                </span>
              </div>
              
              {/* Animated Progress bar */}
              <div className="w-full bg-muted/30 rounded-full h-2 sm:h-3 overflow-hidden relative">
                <motion.div 
                  className={`h-full rounded-full ${
                    progressPercent === 100 
                      ? 'bg-gradient-to-r from-accent via-primary to-accent' 
                      : 'bg-gradient-to-r from-primary to-accent'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercent}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
                {/* Shimmer overlay */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                />
              </div>
              
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                {progressPercent === 100 ? <><Trophy className="w-3 h-3 text-accent shrink-0" /> <span className="truncate">Complete! Amazing work!</span></> :
                 progressPercent >= 75 ? <><Zap className="w-3 h-3 text-primary shrink-0" /> <span className="truncate">Almost there!</span></> :
                 progressPercent >= 50 ? <><Target className="w-3 h-3 text-primary shrink-0" /> <span className="truncate">Halfway done!</span></> :
                 progressPercent > 0 ? <><Sparkles className="w-3 h-3 text-primary shrink-0" /> <span className="truncate">Great start!</span></> :
                 <><Star className="w-3 h-3 text-primary shrink-0" /> <span className="truncate">Ready to begin?</span></>}
              </p>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Motivational Banner */}
      <AnimatePresence>
        {streak >= 3 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="p-4 bg-gradient-to-r from-primary/10 via-secondary/5 to-accent/10 border-primary/20 overflow-hidden relative">
              <motion.div
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
              />
              <div className="flex items-center gap-3 relative z-10">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                >
                  <Star className="w-6 h-6 text-primary" />
                </motion.div>
                <div className="min-w-0">
                  <p className="font-semibold text-foreground flex items-center gap-1 flex-wrap">
                    {streak >= 30 ? <><Zap className="w-4 h-4 text-secondary shrink-0" /> You're absolutely crushing it! Incredible consistency!</> :
                     streak >= 14 ? <><Trophy className="w-4 h-4 text-secondary shrink-0" /> Two weeks strong! Your habits are becoming second nature!</> :
                     streak >= 7 ? <><Sparkles className="w-4 h-4 text-primary shrink-0" /> One week streak! You're doing amazing!</> :
                     <><Star className="w-4 h-4 text-accent shrink-0" /> You're building momentum! Keep it up!</>}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Consistency is the key to lasting transformation
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
